import { useEffect, useState } from 'react'
import { api, type MappingConfig } from '../lib/api'
import { currentPeriod } from '../lib/format'
import { Panel, Field, Alert, Loading, Empty } from '../components/ui'

/** 各同步目標可用的系統欄位（前端提示用） */
const AVAILABLE_FIELDS: Record<string, string[]> = {
  summary: ['row_no', 'client_name', 'contract_code', 'work_order_code', 'writer_name', 'owner_name', 'job_type_name', 'qty', 'unit_price', 'amount'],
  billing: ['row_no', 'client_name', 'work_order_code', 'acceptance_code', 'contract_code', 'writer_name', 'job_type_name', 'qty', 'unit_price', 'amount', 'note'],
  evaluation: ['row_no', 'period', 'owner_name', 'client_name', 'contract_code', 'case_link', 'writer_name', 'ask_writer_on', 'due_on', 'eip_due_on', 'qty', 'unit_price', 'amount', 'work_order_code'],
}

export default function SyncSettings() {
  const [mappings, setMappings] = useState<MappingConfig[] | null>(null)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [test, setTest] = useState<any>(null)
  const [log, setLog] = useState<any[]>([])
  const [period, setPeriod] = useState(currentPeriod())

  const load = () => {
    api.get<MappingConfig[]>('/sync/mappings').then(setMappings).catch((e) => setError(e.message))
    api.get<any[]>('/sync/log').then(setLog).catch(() => {})
  }

  useEffect(load, [])

  const runTest = async () => {
    setError(''); setTest(null)
    try {
      setTest(await api.get('/sync/test'))
    } catch (e: any) {
      setError(e.message)
    }
  }

  const saveMapping = async (m: MappingConfig) => {
    setError(''); setMsg('')
    try {
      await api.put(`/sync/mappings/${m.target}`, {
        spreadsheet_id: m.spreadsheet_id,
        sheet_name: m.sheet_name,
        header_row: m.header_row,
        start_row: m.start_row,
        max_rows: m.max_rows,
        write_mode: m.write_mode,
        enabled: !!m.enabled,
        fields: m.fields.map((f) => ({ field: f.field, col_letter: f.col_letter })),
      })
      setMsg(`「${m.label}」設定已儲存`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const preview = async (target: string) => {
    setError(''); setMsg('')
    try {
      const res = await api.post<any>('/sync/preview', { target, period })
      setMsg(
        `${res.range}：${res.row_count} 列，${res.changes.length} 個儲存格會變動、${res.unchanged} 個不變` +
        (res.truncated_warning ? `\n⚠ ${res.truncated_warning}` : '') +
        (res.changes.length
          ? `\n前 10 筆變動：\n` +
            res.changes.slice(0, 10).map((c: any) => `  ${c.field} 第${c.row}列：「${c.before}」→「${c.after}」`).join('\n')
          : '')
      )
    } catch (e: any) {
      setError(e.message)
    }
  }

  const patch = (idx: number, p: Partial<MappingConfig>) =>
    setMappings((list) => list!.map((m, i) => (i === idx ? { ...m, ...p } : m)))

  return (
    <>
      <div className="page-head">
        <h2>Google Sheet 同步設定</h2>
        <span className="hint">欄位對應存在資料庫，改設定即可，不需要改程式碼</span>
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>{msg}</div>}

      <Panel title="連線測試">
        <div className="btn-row">
          <button className="primary" onClick={runTest}>測試 Service Account 連線</button>
          <span className="hint">
            會列出目標試算表的所有分頁名稱，並顯示 token 是否來自 KV 快取。
          </span>
        </div>
        {test && (
          <div className="alert ok" style={{ marginTop: 12 }}>
            <div>試算表：{test.spreadsheetId}</div>
            <div>分頁：{test.sheets.join('、')}</div>
            <div>{test.note}</div>
          </div>
        )}
      </Panel>

      <Panel title="同步預覽的月份">
        <div style={{ maxWidth: 200 }}>
          <Field label="月份">
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" />
          </Field>
        </div>
      </Panel>

      {!mappings ? (
        <Loading />
      ) : (
        mappings.map((m, idx) => (
          <Panel
            key={m.target}
            title={`${m.label}（${m.target}）`}
            actions={
              <div className="btn-row">
                <button onClick={() => preview(m.target)}>預覽</button>
                <button className="primary" onClick={() => saveMapping(m)}>儲存設定</button>
              </div>
            }
          >
            {!m.enabled && (
              <div className="alert warn">
                此目標的同步目前為<b>停用</b>狀態。欄位位置確認無誤後再勾選「啟用」，避免誤蓋既有資料。
              </div>
            )}

            <div className="grid c4">
              <Field label="試算表 ID">
                <input value={m.spreadsheet_id} onChange={(e) => patch(idx, { spreadsheet_id: e.target.value })} />
              </Field>
              <Field label="分頁名稱">
                <input value={m.sheet_name} onChange={(e) => patch(idx, { sheet_name: e.target.value })} />
              </Field>
              <Field label="標題列">
                <input type="number" value={m.header_row} onChange={(e) => patch(idx, { header_row: Number(e.target.value) })} />
              </Field>
              <Field label="資料起始列">
                <input type="number" value={m.start_row} onChange={(e) => patch(idx, { start_row: Number(e.target.value) })} />
              </Field>
            </div>

            <div className="grid c4" style={{ marginTop: 12 }}>
              <Field label="資料區列數上限（避免蓋到簽核區）">
                <input type="number" value={m.max_rows ?? ''} onChange={(e) => patch(idx, { max_rows: e.target.value === '' ? null : Number(e.target.value) })} />
              </Field>
              <Field label="寫入模式">
                <select value={m.write_mode} onChange={(e) => patch(idx, { write_mode: e.target.value })}>
                  <option value="replace_block">replace_block（整塊覆寫，多餘列清空）</option>
                  <option value="append">append（只寫實際列數）</option>
                </select>
              </Field>
              <Field label="啟用同步">
                <select value={m.enabled ? '1' : '0'} onChange={(e) => patch(idx, { enabled: Number(e.target.value) })}>
                  <option value="0">停用</option>
                  <option value="1">啟用</option>
                </select>
              </Field>
            </div>

            <h4 style={{ margin: '18px 0 8px', fontSize: 13 }}>欄位對應</h4>
            <div className="table-wrap">
              <table className="data" style={{ maxWidth: 520 }}>
                <thead>
                  <tr>
                    <th>系統欄位</th>
                    <th style={{ width: 90 }}>目標欄</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {m.fields.map((f, fi) => (
                    <tr key={fi}>
                      <td>
                        <select
                          value={f.field}
                          onChange={(e) =>
                            patch(idx, { fields: m.fields.map((x, i) => (i === fi ? { ...x, field: e.target.value } : x)) })
                          }
                        >
                          {(AVAILABLE_FIELDS[m.target] ?? []).map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          value={f.col_letter}
                          maxLength={3}
                          onChange={(e) =>
                            patch(idx, { fields: m.fields.map((x, i) => (i === fi ? { ...x, col_letter: e.target.value.toUpperCase() } : x)) })
                          }
                        />
                      </td>
                      <td>
                        <button className="danger" onClick={() => patch(idx, { fields: m.fields.filter((_, i) => i !== fi) })}>刪</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <button
                onClick={() =>
                  patch(idx, {
                    fields: [...m.fields, { field: (AVAILABLE_FIELDS[m.target] ?? ['row_no'])[0], col_letter: 'A', sort: m.fields.length + 1 }],
                  })
                }
              >
                ＋ 新增欄位對應
              </button>
            </div>
          </Panel>
        ))
      )}

      <Panel title="最近同步紀錄">
        {log.length === 0 ? (
          <Empty>還沒有同步紀錄</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>時間</th>
                <th>目標</th>
                <th>月份</th>
                <th>範圍</th>
                <th className="num">列數</th>
                <th>結果</th>
                <th>操作者</th>
              </tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id}>
                  <td>{l.created_at}</td>
                  <td>{l.target}</td>
                  <td>{l.period}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.range_a1}</td>
                  <td className="num">{l.row_count}</td>
                  <td style={l.ok ? undefined : { color: 'var(--bad)' }}>{l.message}</td>
                  <td>{l.operator}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  )
}
